import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { inputCls } from "../components/Form";
import { errMsg, money, useApi, loadFailed } from "../lib/api";
import { useMe } from "../lib/me";
import { ReminderSetting, YouTubeConnect } from "../components/admin/Integrations";

export function AdminSales() {
  const api = useApi();
  const { me, refresh } = useMe();
  const [d, setD] = useState(null);
  const [price, setPrice] = useState(String(me.membership_price));
  useEffect(() => { api.get("/admin/sales").then((r) => setD(r.data)).catch(loadFailed("sales")); }, [api]);
  const savePrice = async () => {
    try { await api.patch("/settings", { membership_price: parseFloat(price) }); toast.success("Membership price updated"); refresh(); }
    catch (e) { toast.error(errMsg(e, "Price must be between $1 and $1000")); }
  };
  return (
    <div className="spotlight min-h-screen" data-testid="admin-sales-page">
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-10 space-y-8">
        <div><span className="mono-label text-yellow-300">Admin</span><h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">Sales</h1></div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="panel p-5"><p className="mono-label">Total revenue</p><p className="mt-2 mono text-3xl font-bold text-yellow-400" data-testid="sales-total">{money(d?.total)}</p></div>
          <div className="panel p-5"><p className="mono-label">Payments</p><p className="mt-2 mono text-3xl font-bold" data-testid="sales-count">{d?.count ?? 0}</p></div>
          <div className="panel p-5 space-y-2" data-testid="membership-price-setting">
            <p className="mono-label">Monthly membership (USD)</p>
            <div className="flex gap-2"><input type="number" min="1" step="1" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="membership-price-input" />
              <button onClick={savePrice} data-testid="membership-price-save-btn" className="rounded-[3px] bg-yellow-400 px-4 font-bold text-black hover:bg-yellow-300">Save</button></div>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <YouTubeConnect />
          <ReminderSetting />
        </div>
        <section className="panel p-5" data-testid="sales-by-teacher">
          <p className="font-bold mb-3">By teacher <span className="text-xs font-normal text-zinc-500">— pay teachers their share outside the app</span></p>
          {(d?.by_teacher || []).length === 0 && <p className="text-sm text-zinc-500">No payments yet.</p>}
          {d?.by_teacher.map((t) => (
            <div key={t.teacher_id || "camp"} className="flex items-center gap-3 py-2 border-b border-[#2d2d38] last:border-0">
              <span className="flex-1">{t.teacher_name}</span><span className="mono text-xs text-zinc-400">{t.count} sales</span><span className="mono font-bold text-yellow-300">{money(t.total)}</span>
            </div>
          ))}
        </section>
        <section className="panel p-5 overflow-x-auto" data-testid="sales-transactions">
          <p className="font-bold mb-3">Every payment</p>
          <table className="w-full text-sm">
            <thead><tr className="mono-label text-left"><th className="py-2">Date</th><th>Buyer</th><th>Type</th><th>Item</th><th>Teacher</th><th className="text-right">Amount</th></tr></thead>
            <tbody>{d?.transactions.map((t) => (
              <tr key={t.session_id} className="border-t border-[#2d2d38]">
                <td className="py-2 mono text-xs">{new Date(t.created_at).toLocaleDateString()}</td><td>{t.user_email}</td><td className="capitalize">{t.kind}</td>
                <td>{t.item_title}</td><td>{t.teacher_name || "—"}</td><td className="text-right mono">{money(t.amount)}</td>
              </tr>
            ))}</tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const api = useApi();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setD(r.data))
    .catch((e) => toast.error(errMsg(e, "Couldn't load users"))), [api]);
  useEffect(() => { load(); }, [load]);
  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.user_id}/role`, { role }); toast.success(`${u.name} is now ${role}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="spotlight min-h-screen" data-testid="admin-users-page">
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-10">
        <span className="mono-label text-yellow-300">Admin</span><h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">Users</h1>
        <p className="mt-3 text-zinc-400">Make someone a host so they can run rooms, shows, lessons and courses.</p>
        <div className="mt-8 panel divide-y divide-[#2d2d38]">
          {d?.users.map((u) => (
            <div key={u.user_id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`user-row-${u.user_id}`}>
              <div className="flex-1 min-w-[200px]"><p className="font-semibold">{u.name}</p><p className="mono text-xs text-zinc-400">{u.email}</p></div>
              {(u.membership_until || "") > new Date().toISOString() && <span className="mono text-[10px] text-[#39ff6a]">MEMBER</span>}
              <select value={u.role} onChange={(e) => setRole(u, e.target.value)} data-testid={`user-role-select-${u.user_id}`} className="rounded-[3px] bg-black border border-zinc-800 px-3 py-1.5 text-sm">
                {d.roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
